import type { ComponentType } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getAvailableFreeDoubtsCount,
  getLatestExamDetails,
  // loadUserSession,
  useGetAinaAvailableFreeDoubtsFromSource,
} from "@oneapp/data";
import {
  mapModuleDataToDoubtSession,
  type ModuleDataDto as DoubtsModuleDataDto,
} from "@repo/doubts-web";
import {
  ainaMicDoubtsPropsFromRemoteConfig,
  buildAinaMentorExamPreference,
  extractLatestAinaExamId,
  // parseAinaIntroMessageFromLoadSessionResponse,
  // parseAinaIsNewSessionFromLoadSessionResponse,
  resolveAinaHostFlow,
  resolveAinaTenantForBundle,
  ROUTESWEB,
  consumeAinaReturnPath,
  shouldShowAinaCameraDoubtOnboarding,
} from "@repo/utils";

import { AinaCameraDoubtFlow } from "./AinaCameraDoubtFlow";
import { AinaUploadQuestionIntroModal } from "../components/aina-intro";
// import { buildLoadAinaUserSessionBody } from "../lib/aina/buildLoadAinaUserSessionBody";
import { sendAinaWidgetOpenEvent } from "../lib/aina/sendAinaWidgetOpenEvent";
import type { ModuleDataDto } from "../store/useAuthStore";
import { useNavigate } from "react-router-dom";
import { remoteConfigService } from "@/services/remote-config";

const AUTH_STORAGE_KEY = "auth-storage";
export const AINA_AGENT_VOICE = "AINA_VOICE";
export const AINA_AGENT_MENTOR = "AINA_MENTOR";

type PersistedAuthStorage = {
  state?: Partial<ModuleDataDto> & {
    token?: string | null;
    refreshToken?: string | null;
  };
};

function readStoredModuleData(fallback: ModuleDataDto): ModuleDataDto {
  if (typeof window === "undefined") {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return fallback;

    const parsed = JSON.parse(raw) as PersistedAuthStorage;
    const state = parsed.state;
    if (!state) return fallback;

    return {
      accessToken: state.token ?? fallback.accessToken,
      refreshToken: state.refreshToken ?? fallback.refreshToken,
      accountsData: state.accountsData ?? fallback.accountsData,
      profilesData: state.profilesData ?? fallback.profilesData,
      enrolledPrograms: state.enrolledPrograms ?? fallback.enrolledPrograms,
      setSessionData: fallback.setSessionData,
    } satisfies ModuleDataDto;
  } catch {
    return fallback;
  }
}

export type AinaHostBridgeProps = {
  env?: string;
  dataDTO: ModuleDataDto;
  agent?: string;
};

export function createAinaHostBridge(
  Inner: ComponentType<Record<string, unknown>>,
): ComponentType<AinaHostBridgeProps> {
  return function AinaHostBridge({
    dataDTO: fallbackDataDTO,
    agent = AINA_AGENT_VOICE,
  }: AinaHostBridgeProps) {
    const navigate = useNavigate();
    const [ainaCameraFlowOpen, setAinaCameraFlowOpen] = useState(false);
    const [uploadIntroOpen, setUploadIntroOpen] = useState(false);
    const [innerRemountKey, setInnerRemountKey] = useState(0);
    const dataDTO = readStoredModuleData(fallbackDataDTO);

    const doubtSession = useMemo(
      () => mapModuleDataToDoubtSession(dataDTO as DoubtsModuleDataDto),
      [dataDTO],
    );

    const { data: ainaDoubtsRes } = useGetAinaAvailableFreeDoubtsFromSource(
      {
        userId: String(doubtSession.uamId ?? ""),
        tenantId: doubtSession.dcNumericTenantId?.trim() || "1",
        source: "AINA",
      },
      {
        request: { clientName: "doubtsDCAPI" },
        query: {
          enabled: Boolean(String(doubtSession.uamId ?? "").trim()),
          staleTime: 0,
          refetchOnMount: "always",
          gcTime: 0,
        },
      },
    );

    const noOfDoubts = getAvailableFreeDoubtsCount(ainaDoubtsRes);

    const profiles = dataDTO.profilesData?.unified_account_details?.profiles;
    const profile =
      profiles?.find((item) => item.is_default_profile) ?? profiles?.[0];
    const accountDetails =
      dataDTO.profilesData?.unified_account_details?.user_account_details;
    const program =
      dataDTO.enrolledPrograms?.find((item) => item.isDefault) ??
      dataDTO.enrolledPrograms?.[0];

    const firstName = (accountDetails?.first_name as string | undefined) ?? "";
    const lastName = (accountDetails?.last_name as string | undefined) ?? "";
    const studentName = `${firstName} ${lastName}`.trim();
    const studentPhone = accountDetails?.phone ?? "";

    const authToken = dataDTO.accessToken ?? "";

    const studentGrade =
      program?.gradeName ??
      program?.mdsGradeName ??
      accountDetails?.grade?.name ??
      program?.gradeId?.toString() ??
      program?.mdsGradeId?.toString() ??
      "";

    const tenant = useMemo(() => {
      const ua = accountDetails as Record<string, unknown> | undefined;
      const userTenantRaw = ua?.tenant_id ?? ua?.tenantId;
      const userTenantId =
        typeof userTenantRaw === "string" || typeof userTenantRaw === "number"
          ? userTenantRaw
          : undefined;
      return resolveAinaTenantForBundle({
        userTenantId,
        programTenantId: program?.tenant_id,
        profileTenantId: profile?.tenant_id,
        doubtTenantKey: doubtSession.tenantId,
        doubtNumericTenantId: doubtSession.dcNumericTenantId,
      });
    }, [
      accountDetails,
      program?.tenant_id,
      profile?.tenant_id,
      doubtSession.tenantId,
      doubtSession.dcNumericTenantId,
    ]);

    const userId = profile?.uam_id?.toString() ?? "";

    const latestExamQuery = useQuery({
      queryKey: ["aina-latest-exam-bridge", userId, agent],
      queryFn: () =>
        getLatestExamDetails(
          { student_id: userId },
          {},
          { clientName: "aina" },
        ),
      enabled: Boolean(userId.trim()),
      staleTime: 60_000,
    });
    const latestExamRes = latestExamQuery.data;

    // const loadUserSessionQuery = useQuery({
    //   queryKey: [
    //     "aina-host-bridge",
    //     "load-user-session",
    //     userId,
    //     agent,
    //     latestExamRes,
    //   ],
    //   queryFn: async () => {
    //     const body = buildLoadAinaUserSessionBody({
    //       agent,
    //       latestExamId: extractLatestAinaExamId(latestExamRes),
    //       latestExamDetailsRes: latestExamRes,
    //     });
    //     if (!body) throw new Error("Not authenticated");
    //     return loadUserSession(body, { clientName: "aina" });
    //   },
    //   enabled: Boolean(
    //     authToken?.trim() &&
    //     userId.trim() &&
    //     (!isMentorAgent || latestExamQuery.isFetched),
    //   ),
    //   staleTime: 60_000,
    // });

    // const introMessage = useMemo(
    //   () =>
    //     parseAinaIntroMessageFromLoadSessionResponse(loadUserSessionQuery.data),
    //   [loadUserSessionQuery.data],
    // );
    // const isNewSession = useMemo(
    //   () =>
    //     parseAinaIsNewSessionFromLoadSessionResponse(loadUserSessionQuery.data),
    //   [loadUserSessionQuery.data],
    // );

    const latestExamId = extractLatestAinaExamId(latestExamRes);

    const examPreference = useMemo(() => {
      return buildAinaMentorExamPreference(
        program,
        latestExamId,
        latestExamRes,
      );
    }, [agent, latestExamId, latestExamRes, program]);

    const onClose = useCallback(() => {
      const returnTo = consumeAinaReturnPath();
      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate(`/${ROUTESWEB.APP.DASHBOARD}`);
      }
    }, [navigate]);

    const remoteFlags = remoteConfigService.getAllValues();
    const ainaMicDoubts = useMemo(
      () => ainaMicDoubtsPropsFromRemoteConfig(agent, remoteFlags),
      [agent, remoteFlags],
    );

    const disableMic = ainaMicDoubts.disableMic;
    const disableDoubts = ainaMicDoubts.disableDoubts;

    const onEvent = useCallback(
      (e: unknown) => {
        const resolution = resolveAinaHostFlow(e, Number(noOfDoubts));
        if (resolution.kind === "open_mic_input") {
          if (import.meta.env.DEV) {
            console.debug("[AINA] handleAinaFlow: OPEN_MIC_INPUT");
          }
          return;
        }
        if (resolution.kind === "doubts_restart") {
          setInnerRemountKey((k) => k + 1);
          return;
        }
        if (resolution.kind === "open_doubts_section" && resolution.noQuota) {
          setInnerRemountKey((k) => k + 1);
          return;
        }
        if (
          resolution.kind === "open_camera_doubt" ||
          (resolution.kind === "open_doubts_section" && !resolution.noQuota)
        ) {
          if (shouldShowAinaCameraDoubtOnboarding(Number(noOfDoubts))) {
            setUploadIntroOpen(true);
          } else {
            setAinaCameraFlowOpen(true);
          }
          return;
        }
      },
      [noOfDoubts],
    );

    useEffect(() => {
      if (!authToken?.trim()) return;
      sendAinaWidgetOpenEvent();
    }, [authToken]);

    console.log("aina props:", {
      agent,
      userId,
      studentName,
      studentGrade,
      studentPhone,
      examPreference,
      authToken,
      noOfDoubts,
      tenant,
      platform: "WEB",
      disableMic,
      disableDoubts,
      // introMessage,
      // isNewSession,
    });
    return (
      <>
        <AinaUploadQuestionIntroModal
          open={uploadIntroOpen}
          noOfDoubts={Number(noOfDoubts)}
          onClose={() => setUploadIntroOpen(false)}
          onLetsTry={() => {
            setUploadIntroOpen(false);
            setAinaCameraFlowOpen(true);
          }}
        />
        <AinaCameraDoubtFlow
          open={ainaCameraFlowOpen}
          onClose={() => setAinaCameraFlowOpen(false)}
          doubtSession={doubtSession}
          agent={agent}
        />
        <Inner
          key={innerRemountKey}
          agent={agent}
          platform="WEB"
          authToken={authToken}
          userId={userId}
          tenant={tenant}
          studentName={studentName}
          studentPhone={studentPhone}
          studentGrade={studentGrade}
          examPreference={examPreference}
          onClose={onClose}
          onEvent={onEvent}
          noOfDoubts={noOfDoubts}
          disableMic={disableMic}
          disableDoubts={disableDoubts}
          // isNewSession={isNewSession}
          // introMessage={introMessage}
        />
      </>
    );
  };
}
